import React, { useEffect, useRef, useState } from 'react'

import PermissionAccessGuard from '../../Shared/PermissionAccessGuard/PermissionAccessGuard'

export interface DetailField {
  displayName: string
  displayUrl?: string
  dataKey: string
}

export interface ButtonConfig {
  buttonType: 'cta' | 'icon'
  buttonLabel?: string
  buttonIcon?: string
  buttonUrl: string
  // Optional PermissionsV2 name (e.g. "Assets.CreateCase"). When set, the button is hidden unless the current account grants it.
  permission?: string
}

export type AssetDetailsProps = {
  data?: Record<string, unknown> | null
  detailFields?: string
  showButtons?: boolean
  buttons?: ButtonConfig[]
  onReportIssueClick?: () => void
  viewMode?: 'all' | 'header' | 'rows'
}

const LIFECYCLE_STATUS_COLORS: { label: string; color: string }[] = [
  { color: '#0d0c0b', label: 'obsolete' },
  { color: '#0079D0', label: 'Active' },
  { color: '#320904ea', label: 'Limited' },
  { color: '#d5e008', label: 'Classic' }
]

function getLifecycleStatusColor(value: string): string | undefined {
  const normalizedValue = value.trim().toLowerCase()
  return LIFECYCLE_STATUS_COLORS.find(
    s => s.label.toLowerCase() === normalizedValue
  )?.color
}

function parseDetailFields(raw?: string): DetailField[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed as DetailField[]
  } catch {
    return []
  }
}

function stripWrappingQuotes(value: string): string {
  const trimmed = value.trim()
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1)
  }
  return trimmed
}

function getFieldValue(data: Record<string, unknown> | null | undefined, dataKey: string): string {
  if (!data) return '-'
  const value = data[dataKey]
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'string') return stripWrappingQuotes(value)
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function getBooleanField(data: Record<string, unknown> | null | undefined, dataKey: string): boolean {
  return data?.[dataKey] === true
}

function isReportIssueButton(button: ButtonConfig): boolean {
  const icon = (button.buttonIcon || '').toLowerCase()
  const label = (button.buttonLabel || '').toLowerCase()
  return icon.includes('alert') || label.includes('report data issue')
}

function appendSiteIdToUrl(displayUrl: string, siteId?: string): string {
  if (!siteId) return displayUrl

  const normalizedSiteId = siteId.trim()
  if (!normalizedSiteId || normalizedSiteId === '-') return displayUrl

  const encodedSiteId = encodeURIComponent(normalizedSiteId)

  if (displayUrl.includes('{siteId}')) {
    return displayUrl.replace('{siteId}', encodedSiteId)
  }

  const hashIndex = displayUrl.indexOf('#')
  const baseUrl = hashIndex >= 0 ? displayUrl.slice(0, hashIndex) : displayUrl
  const hashPart = hashIndex >= 0 ? displayUrl.slice(hashIndex) : ''
  const separator = baseUrl.endsWith('/') ? '' : '/'
  return `${baseUrl}${separator}${encodedSiteId}${hashPart}`
}

export const AssetDetails: React.FC<AssetDetailsProps> = ({
  data,
  detailFields: detailFieldsRaw,
  showButtons = false,
  buttons = [],
  onReportIssueClick,
  viewMode = 'all',
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement | null>(null)

  const onAuthoredButtonClick = (button: ButtonConfig) => (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (!isReportIssueButton(button)) return
    e.preventDefault()
    e.stopPropagation()
    onReportIssueClick?.()
  }

  const onMenuToggle = (): void => setIsMobileMenuOpen(open => !open)

  const onReportIssueMenuClick = (): void => {
    setIsMobileMenuOpen(false)
    onReportIssueClick?.()
  }

  const onMenuItemMouseDown = (e: React.MouseEvent): void => e.stopPropagation()

  useEffect(() => {
    if (!isMobileMenuOpen) return

    const onDocumentClick = (event: MouseEvent) => {
      if (!mobileMenuRef.current?.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [isMobileMenuOpen])

  const detailFields = parseDetailFields(detailFieldsRaw)
  const serialNumberField = detailFields.find(field => field.dataKey.toLowerCase() === 'serialnumber')
  const detailRows = detailFields.filter(field => field.dataKey.toLowerCase() !== 'serialnumber')

  const siteId = (() => {
    if (!data?.siteId) return undefined
    if (typeof data.siteId === 'string') return data.siteId
    if (typeof data.siteId === 'number' || typeof data.siteId === 'boolean') return String(data.siteId)
    return undefined
  })()

  const reportIssueButton = buttons.find(isReportIssueButton)
  const isSpare = getBooleanField(data, 'spare')
  const isDecom = getBooleanField(data, 'decom')

  const showHeader = Boolean(serialNumberField || showButtons)
  const shouldRenderHeader = showHeader && viewMode !== 'rows'
  const shouldRenderRows = viewMode !== 'header'

  return (
    <div className="cop-asset-details__details">
      {shouldRenderHeader ? (
        <div className="cop-asset-details__header">
          {serialNumberField && (
            <div className="cop-asset-details__authored-title">
              <h2 className="cop-asset-details__serial-number">{`${serialNumberField.displayName}: ${getFieldValue(data, serialNumberField.dataKey)}`}</h2>
            </div>
          )}

          <div className="cop-asset-details__header-actions">
            {showButtons &&
              buttons.map((button) => {
                const hasLabel = !!button.buttonLabel?.trim()
                const buttonNode = (
                  <a
                    key={button.buttonUrl}
                    href={button.buttonUrl || '#'}
                    className={`cop-asset-details__button cop-asset-details__button--${button.buttonType || 'cta'}${hasLabel ? '' : ' cop-asset-details__button--icon-only'}${isReportIssueButton(button) ? ' cop-asset-details__button--report-issue' : ''}`}
                    aria-label={hasLabel ? undefined : button.buttonIcon || 'Action'}
                    onClick={onAuthoredButtonClick(button)}
                  >
                    {button.buttonIcon && <span className={`icon icon-${button.buttonIcon}`} />}
                    {button.buttonLabel}
                  </a>
                )
                if (button.permission) {
                  return (
                    <PermissionAccessGuard key={button.buttonUrl} permission={button.permission}>
                      {buttonNode}
                    </PermissionAccessGuard>
                  )
                }
                return buttonNode
              })}

            {reportIssueButton && (
              <div className="cop-asset-details__mobile-menu" ref={mobileMenuRef}>
                <button
                  type="button"
                  className="cop-asset-details__menu-btn"
                  aria-label="More options"
                  aria-expanded={isMobileMenuOpen}
                  onClick={onMenuToggle}
                >
                  &#8942;
                </button>
                {isMobileMenuOpen && (
                  <div className="cop-asset-details__mobile-menu-dropdown" role="menu" aria-label="More options">
                    <button
                      type="button"
                      className="cop-asset-details__mobile-menu-item"
                      role="menuitem"
                      onMouseDown={onMenuItemMouseDown}
                      onClick={onReportIssueMenuClick}
                    >
                      {reportIssueButton.buttonLabel || 'Report Data Issue'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {isSpare && <span className="cop-asset-details__chip cop-asset-details__chip--spare">Spare</span>}
            {isDecom && <span className="cop-asset-details__chip cop-asset-details__chip--decom">Decom</span>}
          </div>
        </div>
      ) : null}

      {shouldRenderRows && (
        <div className="cop-asset-details__rows-container">
          {detailRows.map((field) => {
            const isPowerBase = field.dataKey.toLowerCase() === 'powerbase'
            const value = isPowerBase
              ? `${getFieldValue(data, field.dataKey)}/${getFieldValue(data, 'highVoltage')}`
              : getFieldValue(data, field.dataKey)
            const isLifecycle = field.dataKey === 'lifecycleStatus'
            const dotColor = isLifecycle && value !== '-' ? getLifecycleStatusColor(value) : undefined
            const isSiteName = field.dataKey.toLowerCase() === 'sitename'
            const urlForSiteId = isSiteName && field.displayUrl ? appendSiteIdToUrl(field.displayUrl, siteId) : field.displayUrl
            const linkHref = field.displayUrl ? urlForSiteId : undefined

            let renderedValue: React.ReactNode
            if (linkHref) {
              renderedValue = (
                <a href={linkHref} className="cop-asset-details__link">
                  {value}
                </a>
              )
            } else if (isLifecycle && dotColor) {
              renderedValue = (
                <span className="cop-asset-details__status">
                  <span
                    className="cop-asset-details__status-dot"
                    style={{ '--status-dot-color': dotColor } as React.CSSProperties}
                  />
                  {value}
                </span>
              )
            } else {
              renderedValue = value
            }

            return (
              <div key={field.dataKey} className="cop-asset-details__detail-row">
                <span className="cop-asset-details__label">{field.displayName}</span>
                <span className="cop-asset-details__value">
                  {renderedValue}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}